import { FormattedObjective } from "../graphql/generated/graphql";

type ObjectiveInput = FormattedObjective | string | null | undefined;

export type NormalizedObjective = Omit<FormattedObjective, "id" | "sequence" | "evalCriteria" | "videos" | "status"> & {
  id: string;
  sequence: number;
  evalCriteria: string[];
  videos: NonNullable<FormattedObjective["videos"]>;
  status?: string;
};

type NormalizeObjectiveOptions = {
  activityId?: string | null;
  defaultStatus?: string;
  objective: ObjectiveInput;
  index: number;
};

type NormalizeObjectivesOptions = {
  activityId?: string | null;
  defaultStatus?: string;
  objectives?: ObjectiveInput[] | null;
};

const slugifyObjective = (objective: string) =>
  objective
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "objective";

export const getStableObjectiveId = ({
  activityId,
  objective,
  index,
}: {
  activityId?: string | null;
  objective: string;
  index: number;
}) => `${activityId || "activity"}-objective-${index + 1}-${slugifyObjective(objective)}`;

export const normalizeObjective = ({
  activityId,
  defaultStatus,
  objective,
  index,
}: NormalizeObjectiveOptions): NormalizedObjective | null => {
  if (!objective) return null;

  if (typeof objective === "string") {
    return {
      id: getStableObjectiveId({ activityId, objective, index }),
      objective,
      sequence: index + 1,
      evalCriteria: [],
      videos: [],
      ...(defaultStatus ? { status: defaultStatus } : {}),
    };
  }

  if (!objective.objective) return null;

  return {
    ...objective,
    id: objective.id || getStableObjectiveId({ activityId, objective: objective.objective, index }),
    objective: objective.objective,
    sequence: index + 1,
    evalCriteria: Array.isArray(objective.evalCriteria)
      ? objective.evalCriteria.filter((item): item is string => item != null)
      : [],
    videos: objective.videos || [],
    ...(objective.status || defaultStatus ? { status: objective.status || defaultStatus } : {}),
  };
};

export const normalizeObjectives = ({
  activityId,
  defaultStatus,
  objectives,
}: NormalizeObjectivesOptions): NormalizedObjective[] =>
  (objectives ?? []).reduce<NormalizedObjective[]>((normalizedObjectives, objective) => {
    const normalizedObjective = normalizeObjective({
      activityId,
      defaultStatus,
      objective,
      index: normalizedObjectives.length,
    });

    return normalizedObjective ? [...normalizedObjectives, normalizedObjective] : normalizedObjectives;
  }, []);
